import axios from 'axios';
import { config } from 'config/constants';
import { setUser } from 'pages/sing-in/store/actions/auth.types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { handleRegisterWithCustomToken } from 'shared/services/apiSales';
import { setBranch } from 'store/actions/branch.types';
import { isSodaOrigin } from 'utils/soda-origin.utils';

const SODA_REDIRECT_ENABLED =
  process.env.REACT_APP_ENABLE_SODA_REDIRECT === 'true';

function decodePayload(encoded: string) {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as {
    email: string;
    agentStrapiToken: string;
    returnUrl: string;
    agentName: string;
    agentEmail: string;
    agentUserId: number;
    agentRole: string;
    agentAuthId?: string;
    sourceApp?: string;
    redirectPath?: string;
    skipImpersonationLog?: boolean;
    subscriptionsEnabled?: boolean;
    lockToEditPlan?: boolean;
  };
}

type Step =
  | 'decoding'
  | 'impersonating'
  | 'firebase'
  | 'plans'
  | 'dispatching'
  | 'done';

export const AutoImpersonate = () => {
  const dispatch = useDispatch();
  const [step, setStep] = useState<Step>('decoding');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    console.log('[AutoImpersonate] mounted — SODA_REDIRECT_ENABLED:', SODA_REDIRECT_ENABLED);

    if (!SODA_REDIRECT_ENABLED) return;

    async function run() {
      try {
        // ── 1. Decode URL param ──────────────────────────────────────────
        const params = new URLSearchParams(window.location.search);
        const encoded = params.get('d');
        console.log('[AutoImpersonate] d param present:', !!encoded);

        if (!encoded) {
          setErrorMsg('No se encontró el parámetro "d" en la URL.');
          return;
        }

        const payload = decodePayload(encoded);
        console.log('[AutoImpersonate] payload decoded — email:', payload.email, '| hasToken:', !!payload.agentStrapiToken);

        if (!payload.email || !payload.agentStrapiToken || !payload.returnUrl) {
          setErrorMsg(
            `El payload no tiene los campos requeridos. email="${payload.email}" agentStrapiToken="${!!payload.agentStrapiToken}" returnUrl="${payload.returnUrl}"`
          );
          return;
        }

        if (!isSodaOrigin(payload.returnUrl)) {
          setErrorMsg(`returnUrl no pertenece a un origen de confianza: "${payload.returnUrl}"`);
          return;
        }

        // ── 2. Llamar /v2/backoffice/impersonate ─────────────────────────
        setStep('impersonating');
        let sourceOrigin = '';
        try {
          if (payload.returnUrl) sourceOrigin = new URL(payload.returnUrl).origin + '/';
        } catch { /* ignore */ }
        console.log('[AutoImpersonate] calling impersonate API — url:', `${config.merakiCms.url}/v2/backoffice/impersonate`);
        const impersonatePayload = {
          email: payload.email,
          phone: null,
          phone_country_code: null,
          sourceApp: payload.sourceApp,
          sourceOrigin,
          skipImpersonationLog: payload.skipImpersonationLog === true,
        };
        console.log('[AutoImpersonate] impersonate payload:', { ...impersonatePayload, email: undefined });
        const impersonateRes = await axios.post(
          `${config.merakiCms.url}/v2/backoffice/impersonate`,
          impersonatePayload,
          {
            headers: {
              Authorization: `Bearer ${payload.agentStrapiToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const data = impersonateRes.data;
        console.log('[AutoImpersonate] impersonate ok — hasToken:', !!data?.customToken, '| companyId:', data?.company?.id);

        if (data?.needsUserSelection) {
          setErrorMsg(`El email "${payload.email}" coincide con varios usuarios. Se necesita selección manual.`);
          return;
        }
        if (!data?.customToken) {
          setErrorMsg(`La API no devolvió customToken. Respuesta: ${JSON.stringify(data)}`);
          return;
        }

        // ── 3. Firebase custom token exchange ────────────────────────────
        setStep('firebase');
        console.log('[AutoImpersonate] exchanging customToken for Firebase ID token');
        const userWithToken = await handleRegisterWithCustomToken(data.customToken);
        const tknFirebase = await userWithToken.getIdToken();

        // ── 4. Obtener planes ─────────────────────────────────────────────
        setStep('plans');
        console.log('[AutoImpersonate] fetching plans — company id:', data.company?.id);
        const planResponse = await axios.get(
          `${config.merakiCms.url}/company/plans/${data.company.id}`,
          { headers: { Authorization: `Bearer ${payload.agentStrapiToken}` } }
        );
        const plansId: string = Array.isArray(planResponse.data)
          ? planResponse.data.join(',')
          : '';

        // ── 5. Despachar sesión al store ──────────────────────────────────
        setStep('dispatching');

        // Soda normaliza "customer service" (Strapi) → "user-impersonation" internamente.
        // Meraki necesita "customer service" para mostrar el menú correcto (ej. Suscripciones).
        const merakiAgentRole =
          payload.agentRole === 'user-impersonation' ? 'customer service' : payload.agentRole;

        console.log('[AutoImpersonate] dispatching session — uid:', data.auth_id, '| agentRole:', merakiAgentRole);
        dispatch(setBranch(data.country_id.acronym));
        dispatch(
          setUser({
            role: data.role?.name?.toLowerCase() ?? 'authenticated',
            countryAcronym: data.country_id.acronym,
            userId: data.id,
            uid: data.auth_id,
            name: data.name,
            lastname: data.lastname,
            email: data.email,
            accessToken: payload.agentStrapiToken,
            userAuth: {
              phone: data.phone ?? '',
              phone_country_code: data.phone_country_code ?? '',
            },
            agentId: data.agentId,
            companyId: data.company.id,
            plansId,
            formToShow: data.company?.formToShow ?? null,
            tknFirebase,
            isTester: !!data.is_tester,
            photoURL: '',
            agentAuthData: {
              userId: payload.agentUserId,
              uid: payload.agentAuthId ?? '',
              sourceApp: payload.sourceApp ?? 'meraki-frontend',
              sourceOrigin,
              name: payload.agentName,
              lastname: '',
              email: payload.agentEmail,
              accessToken: payload.agentStrapiToken,
              returnUrl: payload.returnUrl,
              subscriptionsEnabled: payload.subscriptionsEnabled === true,
              lockToEditPlan: payload.lockToEditPlan === true,
              branch: data.country_id.acronym,
              userAuth: { phone: '', phone_country_code: '' },
              plansId: '',
              companyId: 0,
              role: merakiAgentRole,
              countryAcronym: '',
              tknFirebase: '',
              isTester: false,
            },
          })
        );

        setStep('done');
        const rawPath = payload.redirectPath;
        const isSafePath = typeof rawPath === 'string' && rawPath.startsWith('/') && !rawPath.startsWith('//');
        const destination = isSafePath ? rawPath : '/settings';
        console.log('[AutoImpersonate] session dispatched — navigating to', destination);
        window.location.replace(destination);
      } catch (error) {
        console.error('[AutoImpersonate] unhandled error at step', step, ':', error);
        setErrorMsg(`Error en paso "${step}": ${(error as Error)?.message ?? String(error)}`);
      }
    }

    run();
  }, []);

  if (!SODA_REDIRECT_ENABLED) {
    return <Navigate to="/login" replace />;
  }

  if (errorMsg) {
    return (
      <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
        <h2 style={{ color: '#c00' }}>Auto-impersonate error</h2>
        <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
          {errorMsg}
        </pre>
        <button onClick={() => window.location.replace('/login')}>
          Ir a login
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', fontFamily: 'monospace', color: '#555' }}>
      Iniciando sesión… ({step})
    </div>
  );
};
